import React, { useEffect, useState } from "react";
import {
  fetchGdCategories, createGdCategory, updateGdCategory, deleteGdCategory,
} from "./adminGdService";
import ImagePickerModal from "../media_images/ImagePickerModal";

const BASE_ROOT = process.env.REACT_APP_BASE_URL_ROOT || "";
const toPublicImgSrc = (url) => {
  if (!url) return "";
  const pub = url.replace(/\/api(?:\/admin)?\/media-images\//, "/api/public/media-images/");
  const src = pub.startsWith("http") ? pub : `${BASE_ROOT}${pub}`;
  console.log("[AdminGdCategoriesPage] img raw:", url, "→ resolved:", src);
  return src;
};

const EMPTY = { name: "", color: "#019095", coverImageId: "", sortOrder: "" };

const AdminGdCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickedUrl, setPickedUrl] = useState("");

  const load = async () => {
    setLoading(true);
    const res = await fetchGdCategories();
    if (res.status === 200) setCategories(Array.isArray(res.data) ? res.data : res.data?.content || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditingId(null); setPickedUrl(""); };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setForm({ name: cat.name, color: cat.color || "#019095", coverImageId: cat.coverImageId || "", sortOrder: cat.sortOrder ?? "" });
    setPickedUrl(cat.coverImageUrl || "");
  };

  const handleImageSelect = (url) => {
    // url is the relative path; extract id from categories or store url for display
    setPickedUrl(url);
    // We store the url as coverImageId placeholder — backend expects Long ID
    // The ImagePickerModal returns url; we need to find the id from the media list
    // For now store url and let user know; ideally ImagePickerModal should return id
    setForm((f) => ({ ...f, coverImageId: url }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return alert("Name is required");
    const payload = {
      name: form.name.trim(),
      color: form.color,
      sortOrder: form.sortOrder !== "" ? Number(form.sortOrder) : null,
      coverImageId: form.coverImageId ? Number(form.coverImageId) || null : null,
    };
    const res = editingId
      ? await updateGdCategory(editingId, payload)
      : await createGdCategory(payload);
    if (res.status === 200 || res.status === 201) { reset(); load(); }
    else alert(res.message || "Save failed");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    const res = await deleteGdCategory(id);
    if (res.status === 200 || res.status === 204) load();
    else alert(res.message || "Delete failed");
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>GD Categories</h2>
      </div>

      {/* Form */}
      <div style={{ background: "#f9f9f9", borderRadius: 8, padding: 16, marginBottom: 24 }}>
        <h4 style={{ margin: "0 0 12px" }}>{editingId ? "Edit Category" : "New Category"}</h4>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <input
            placeholder="Name *" value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            style={{ flex: 1, minWidth: 160, padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 13 }}>Color</label>
            <input type="color" value={form.color}
              onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
              style={{ width: 40, height: 34, border: "none", cursor: "pointer", borderRadius: 4 }}
            />
            <span style={{ fontSize: 12, color: "#666" }}>{form.color}</span>
          </div>
          <input
            type="number" placeholder="Sort Order" value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
            style={{ width: 100, padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc" }}
          />
          <button onClick={() => setShowPicker(true)} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>
            {pickedUrl ? "Change Image" : "Pick Cover Image"}
          </button>
          {pickedUrl && (
            <img src={toPublicImgSrc(pickedUrl)} alt="cover" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6 }} />
          )}
          <button onClick={handleSave} style={{ padding: "7px 18px", background: "#019095", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
            {editingId ? "Update" : "Add"}
          </button>
          {editingId && <button onClick={reset} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: "pointer" }}>Cancel</button>}
        </div>
      </div>

      {/* Table */}
      {loading ? <p>Loading...</p> : (
        <table className="announcement-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Color</th>
              <th>Cover Image</th>
              <th>Sort Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr><td colSpan="5">No categories found</td></tr>
            ) : categories.map((cat) => (
              <tr key={cat.id}>
                <td style={{ fontWeight: 600 }}>{cat.name}</td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 20, height: 20, borderRadius: 4, background: cat.color, border: "1px solid #ddd" }} />
                    <span style={{ fontSize: 12, color: "#666" }}>{cat.color}</span>
                  </div>
                </td>
                <td>
                  {cat.coverImageUrl
                    ? <img src={toPublicImgSrc(cat.coverImageUrl)} alt={cat.name} style={{ width: 48, height: 36, objectFit: "cover", borderRadius: 4 }} />
                    : <span style={{ color: "#bbb", fontSize: 12 }}>—</span>}
                </td>
                <td style={{ textAlign: "center" }}>{cat.sortOrder ?? "—"}</td>
                <td>
                  <button onClick={() => startEdit(cat)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleDelete(cat.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ImagePickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={handleImageSelect}
        defaultImageUse="GENERAL"
      />
    </div>
  );
};

export default AdminGdCategoriesPage;
